import contextLayoutMultiLine, {
  LayoutMultiLineContextType,
} from '@contexts/layoutMultiLine'
import useRenderer from '@hooks/useRenderer'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import { NormalContent } from '@widgets/Multiline/types'
import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import { StyledMultilineProps } from '.'

export interface CustomGridMultilineProps {
  children: NormalContent
}

export type StyledCustomGridMultilineProps = StyledMultilineProps &
  Pick<LayoutMultiLineContextType, 'grid'>

const { LayoutMultiLineContext } = contextLayoutMultiLine

const StyledCustomGridMultiline = styled.div<StyledCustomGridMultilineProps>`
  ${({
    theme: {
      colors: {
        blicktvGrey: { btvGrey_60 },
      },
    },
    isVideoBlock,
    grid,
  }) => {
    if (grid === '4|2|2') {
      return css`
        grid-column: span 6;
        display: grid;
        width: 100%;
        grid-gap: 20px;

        ${desktopCSS(css`
          & {
            grid-template-areas:
              'grid4 grid4 grid2_1'
              'grid4 grid4 grid2_2';
          }
        `)}

        ${tabletCSS(css`
          & {
            grid-template-areas:
              'grid4 grid4'
              'grid2_1 grid2_2';
          }
        `)}

        ${mobileCSS(css`
          & {
            grid-auto-flow: row;
          }
        `)}

        > div:nth-of-type(1) {
          ${desktopCSS(css`
            grid-area: grid4;
          `)}
          ${tabletCSS(css`
            grid-area: grid4;
          `)}
        }

        > div:nth-of-type(2) {
          ${desktopCSS(css`
            grid-area: grid2_1;
          `)}
          ${tabletCSS(css`
            grid-area: grid2_1;
          `)}
        }

        > div:nth-of-type(3) {
          ${desktopCSS(css`
            grid-area: grid2_2;
          `)}
          ${tabletCSS(css`
            grid-area: grid2_2;
          `)}
        }

        ${isVideoBlock &&
        css`
          background-color: ${btvGrey_60};
          ${desktopCSS(css`
            padding: 0 20px 20px 20px;
            margin: 0 -20px;
          `)}
          ${tabletCSS(css`
            padding: 0 52px 52px 52px;
            margin: 0 -52px;
          `)}
          ${mobileCSS(css`
            padding-bottom: 20px;
          `)}
        `}
      `
    } else if (grid === '2|2|4') {
      return css`
        grid-column: span 6;
        display: grid;
        width: 100%;
        grid-gap: 20px;

        ${desktopCSS(css`
          & {
            grid-template-areas:
              'grid2_1 grid4 grid4'
              'grid2_2 grid4 grid4';
          }
        `)}

        ${tabletCSS(css`
          & {
            grid-template-areas:
              'grid2_1 grid2_2'
              'grid4 grid4';
          }
        `)}

        ${mobileCSS(css`
          & {
            grid-auto-flow: row;
          }
        `)}

        > div:nth-of-type(1) {
          ${desktopCSS(css`
            grid-area: grid2_1;
          `)}
          ${tabletCSS(css`
            grid-area: grid2_1;
          `)}
        }

        > div:nth-of-type(2) {
          ${desktopCSS(css`
            grid-area: grid2_2;
          `)}
          ${tabletCSS(css`
            grid-area: grid2_2;
          `)}
        }

        > div:nth-of-type(3) {
          ${desktopCSS(css`
            grid-area: grid4;
          `)}
          ${tabletCSS(css`
            grid-area: grid4;
          `)}
        }

        ${isVideoBlock &&
        css`
          background-color: ${btvGrey_60};
          ${desktopCSS(css`
            padding: 0 20px 20px 20px;
            margin: 0 -20px;
          `)}
          ${tabletCSS(css`
            padding: 0 52px 52px 52px;
            margin: 0 -52px;
          `)}
            ${mobileCSS(css`
            padding-bottom: 20px;
          `)}
        `}
      `
    } else {
      return css``
    }
  }}
`

const CustomGridMultiline: FunctionComponent<CustomGridMultilineProps> = ({
  children,
}) => {
  const Renderer = useRenderer()
  const layoutMultilineContext = useContext(LayoutMultiLineContext)

  if (!layoutMultilineContext || !layoutMultilineContext.grid) {
    return null
  }

  const { isVideoBlock, grid } = layoutMultilineContext

  const childrenWithoutLayoutItemWrapper = children.reduce(
    (acc, { content }) => {
      return [...acc, ...content]
    },
    [] as Record<string, unknown>[]
  )

  const flexConfig = {
    '4|2|2': ['4', '2', '2'],
    '2|2|4': ['2', '2', '4'],
  }

  let currentPositionForFlexAdjustment = 0

  const childrenWithAdjustedFlex = childrenWithoutLayoutItemWrapper.map(
    (child) =>
      (child.kind as string[]).includes('appnexus')
        ? child
        : {
            ...child,
            parentProps: {
              flex: flexConfig[grid][currentPositionForFlexAdjustment++],
            },
          }
  )

  return (
    <StyledCustomGridMultiline isVideoBlock={isVideoBlock} grid={grid}>
      <Renderer>{childrenWithAdjustedFlex}</Renderer>
    </StyledCustomGridMultiline>
  )
}

export default CustomGridMultiline
